/**
 * flightCategory — pure FAA flight-category math shared by the Day View's
 * weather strip (design `docs/design/day-schedule-design-v3.dc.html`).
 *
 * Standard category thresholds (ceiling AND/OR visibility, worst wins):
 *   LIFR  ceiling <  500 ft  or vis < 1 SM
 *   IFR   ceiling < 1000 ft  or vis < 3 SM
 *   MVFR  ceiling ≤ 3000 ft  or vis ≤ 5 SM
 *   VFR   everything better
 *
 * PURE: no fetches, no Date.now(), no store access.
 *
 * @module flightCategory
 */

/** Standard aviation category colors (match the design + industry practice). */
export const CAT_COLORS = {
	VFR: '#2f9e44',
	MVFR: '#1971c2',
	IFR: '#e03131',
	LIFR: '#c2255c'
};

export const METERS_PER_SM = 1609.344;
export const FT_PER_M = 3.28084;

/** Cloud amounts that constitute a ceiling. */
const CEILING_AMOUNTS = ['BKN', 'OVC', 'VV'];

/**
 * Lowest ceiling (ft AGL) from cloud layers, or null when no ceiling.
 * @param {Array<{amount:string, baseFt:number|null}>} layers
 */
export function ceilingOf(layers) {
	let ceil = null;
	(layers || []).forEach(l => {
		if (!l || typeof l.baseFt !== 'number') return;
		const amt = String(l.amount || '').toUpperCase();
		if (!CEILING_AMOUNTS.some(c => amt.startsWith(c))) return;
		if (ceil === null || l.baseFt < ceil) ceil = l.baseFt;
	});
	return ceil;
}

/**
 * Derive the flight category.
 * @param {number|null} visSM     Visibility in statute miles (null = unlimited/unknown → ignore).
 * @param {number|null} ceilingFt Ceiling in feet (null = no ceiling → ignore).
 * @returns {'VFR'|'MVFR'|'IFR'|'LIFR'}
 */
export function deriveCategory(visSM, ceilingFt) {
	const v = typeof visSM === 'number' ? visSM : Infinity;
	const c = typeof ceilingFt === 'number' ? ceilingFt : Infinity;
	if (c < 500 || v < 1) return 'LIFR';
	if (c < 1000 || v < 3) return 'IFR';
	if (c <= 3000 || v <= 5) return 'MVFR';
	return 'VFR';
}

/** `'050@12G24'` from degrees / knots — the design's compact wind line. */
export function windText(dirDeg, spdKt, gustKt) {
	if (typeof spdKt !== 'number' || Number.isNaN(spdKt)) return '';
	const d =
		typeof dirDeg === 'number' && !Number.isNaN(dirDeg)
			? String(Math.round(dirDeg / 10) * 10 || 360).padStart(3, '0')
			: 'VRB';
	return `${d}@${Math.round(spdKt)}${gustKt ? `G${Math.round(gustKt)}` : ''}`;
}

/** `'10SM'` / `'3/4SM'`-style visibility text from statute miles. */
export function visText(visSM) {
	if (typeof visSM !== 'number' || Number.isNaN(visSM)) return '';
	if (visSM >= 10) return '10SM';
	if (visSM >= 1) return `${Math.round(visSM * 2) / 2}SM`;
	return `${Math.round(visSM * 4) / 4}SM`;
}

export default deriveCategory;
